import {
  MenuItem,
  Box,
  CircularProgress,
  Select,
  FormHelperText,
  FormControl,
  FormControlProps,
  SelectProps,
  MenuProps,
} from '@mui/material';
import { ReactNode, SyntheticEvent, useState } from 'react';
import FieldLabel from './FieldLabel';
import { DEFAULT_SELECT_MENU_MAX_HEIGHT } from '../../constants';
import SelectNoBorder from './SelectNoBorder';
import { Typography, colors } from '@sweep-io/sweep-design';
import { ChevronDown, ChevronUp } from '@sweep-io/sweep-design/dist/icons';
import { IconSize } from '@sweep-io/sweep-design/dist/icons/common';

export interface SweepSelectProps {
  id?: string;
  renderValue?: (value: unknown) => ReactNode;
  dataTestId?: string;
  MenuProps?: Partial<MenuProps>;
  label?: string;
  labelTooltip?: ReactNode;
  markLabelAsRequired?: boolean;
  error?: string;
  onOpenCb?: (event: SyntheticEvent) => void;
  onCloseCb?: () => void;
  menuMaxHeight?: string;
  FormControlProps?: FormControlProps;
  SelectProps?: SelectProps;
  removeBorders?: boolean;
  children: ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  autoOpen?: boolean;
  variant?: SelectSizes;
}

export enum SelectSizes {
  large = 'large',
  small = 'small',
  medium = 'medium',
}

const heights: Record<SelectSizes, string> = {
  [SelectSizes.large]: '40px',
  [SelectSizes.small]: '24px',
  [SelectSizes.medium]: '32px',
};

const fontSizes: Record<SelectSizes, string> = {
  [SelectSizes.large]: '14px',
  [SelectSizes.small]: '12px',
  [SelectSizes.medium]: '14px',
};

const iconSizes: Record<SelectSizes, IconSize> = {
  [SelectSizes.large]: 'medium',
  [SelectSizes.small]: 'small',
  [SelectSizes.medium]: 'medium',
};

const SweepSelect = ({
  id,
  renderValue,
  MenuProps,
  dataTestId,
  onOpenCb,
  label,
  labelTooltip,
  markLabelAsRequired,
  error,
  onCloseCb,
  menuMaxHeight = DEFAULT_SELECT_MENU_MAX_HEIGHT,
  FormControlProps = {},
  SelectProps = {},
  removeBorders,
  children,
  disabled,
  isLoading,
  autoOpen,
  variant = SelectSizes.large,
}: SweepSelectProps) => {
  const [isOpen, setIsOpen] = useState(autoOpen);

  const SelectComponent = removeBorders ? SelectNoBorder : Select;

  // Ensure value is always defined
  const safeSelectProps = {
    ...SelectProps,
    value: SelectProps.value ?? '',
  };

  const isSmall = variant === SelectSizes.small;

  const { PaperProps: passedPaperProps, ...restMenuProps } = MenuProps || {};
  const mergedMenuProps = {
    anchorOrigin: {
      vertical: 'bottom' as const,
      horizontal: 'left' as const,
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: 'left' as const,
    },
    ...restMenuProps,
    PaperProps: {
      ...passedPaperProps,
      style: {
        maxHeight: menuMaxHeight,
        ...passedPaperProps?.style,
      },
    },
  };

  return (
    <FormControl {...FormControlProps}>
      {label && (
        <FieldLabel
          label={label}
          id={id}
          markLabelAsRequired={markLabelAsRequired}
          infoTooltipTitle={labelTooltip ?? ''}
          showTooltip={Boolean(labelTooltip)}
        />
      )}
      <SelectComponent
        disabled={disabled}
        open={isOpen}
        id={id}
        IconComponent={() => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingRight: '8px',
              position: 'absolute',
              right: '0',
              pointerEvents: 'none',
            }}
          >
            {isOpen ? (
              <ChevronUp size={iconSizes[variant]} />
            ) : (
              <ChevronDown size={iconSizes[variant]} />
            )}
          </Box>
        )}
        onOpen={(event) => {
          setIsOpen(true);
          onOpenCb && onOpenCb(event);
        }}
        onClose={() => {
          setIsOpen(false);
          onCloseCb && onCloseCb();
        }}
        renderValue={renderValue}
        MenuProps={mergedMenuProps}
        error={Boolean(error)}
        data-testid={dataTestId}
        sx={{
          '&.MuiInputBase-root': {
            minHeight: variant === SelectSizes.large ? undefined : 'unset',
            height: heights[variant],
            fontSize: fontSizes[variant],
          },
          '.MuiInputBase-input': {
            marginRight: isSmall ? undefined : '10px',
            paddingLeft: isSmall ? '8px' : undefined,
          },
          '.MuiSelect-select.MuiSelect-outlined': {
            paddingRight: isSmall ? '24px' : undefined,
          },
        }}
        {...safeSelectProps}
      >
        {isLoading && (
          <MenuItem
            disableRipple
            sx={{
              '&.Mui-disabled': {
                opacity: 1,
              },
              cursor: 'auto',
            }}
          >
            <Box
              display="flex"
              width="100%"
              alignItems="center"
              justifyContent="space-between"
              gap="16px"
            >
              <Typography variant="body" color={colors.grey[500]} fontStyle="italic">
                <CircularProgress
                  size="10px"
                  sx={{
                    marginRight: '14px',
                    color: colors.grey[500],
                  }}
                />
                Loading...
              </Typography>
            </Box>
          </MenuItem>
        )}

        {children}
      </SelectComponent>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SweepSelect;
